import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { PageIdentifier, RouterLibCommand, RouteTranslationMap } from "@fabric-types/lib-router";
import { select, Store } from "@ngrx/store";
import { AppLifeCycleAction } from "@obg/ngx.common";
import {
	CORE_ROUTE,
	CtaModalService,
	CultureConfigService,
	DocumentMetaAction,
	MarketSelector,
	STARTUP_CONTEXT_TOKEN,
	StartupContext,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { DocumentRef, LoggingService, PlatformService } from "@obg/ngx.global.evo";
import { RouteAction, RouteConfig, RouteSelector } from "@obg/ngx.router";
import { DynamicLayoutHandler } from "@obg/ngx.ui.core";
import { ignoreNil } from "@obg/rxjs";
import { Observable, Subscription } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { SplashScreenConfigService } from "./splash-screen.config";

@Component({
	selector: "obg-app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
	@HostBinding("class.obg-app-root-server") isServer = this.platformService.isServer;
	@ViewChild("content", { read: ViewContainerRef, static: true })
	get viewContainerRef(): ViewContainerRef | undefined {
		return this._viewContainerRef;
	}
	set viewContainerRef(value: ViewContainerRef | undefined) {
		this._viewContainerRef = value;
		if (!value) {
			return;
		}
		this.dynamicLayoutHandler.attach(value);
		this.store.dispatch(this.appLifeCycleAction.initSuccess());
	}

	splashScreen$: Observable<boolean> | undefined;
	isLoading = true;

	readonly ctaModalEnabledRoute$ = this.ctaModalService.currentEnabledRoute$();

	private lang = "";
	private _viewContainerRef: ViewContainerRef | undefined;
	private languageCode$$: Subscription | undefined;
	private router$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private dynamicLayoutHandler: DynamicLayoutHandler,
		private appLifeCycleAction: AppLifeCycleAction,
		private marketSelector: MarketSelector,
		private cultureConfig: CultureConfigService,
		private splashScreenConfigService: SplashScreenConfigService,
		private documentRef: DocumentRef,
		private platformService: PlatformService,
		private documentMetaAction: DocumentMetaAction,
		private router: Router,
		private ctaModalService: CtaModalService,
		private loggingService: LoggingService,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {
		afterNextRender(() => {
			this.loggingService.initialize();
		});

		this.splashScreen$ = this.store.pipe(
			filter(() => this.splashScreenConfigService.getSplashScreenEnabled() && !this.startupContext.isBot),
			select(RouteSelector.getCurrent),
			ignoreNil(),
			take(1),
			map((route) => route?.name === CORE_ROUTE.home)
		);
	}

	ngOnInit() {
		const hreflangCultureCodes = this.cultureConfig.getHreflangCultureCodes();

		this.store.dispatch(this.documentMetaAction.setMetaDataFromConfig());

		this.languageCode$$ = this.store.select(this.marketSelector.getCurrent()).subscribe((market) => {
			this.lang = hreflangCultureCodes.includes(market.culture) ? market.culture : market.languageCode;
			this.documentRef.native.documentElement.lang = this.lang;

			this.setupShellRouter();
		});
	}

	ngOnDestroy() {
		this.dynamicLayoutHandler.detach();
		this.languageCode$$?.unsubscribe();
		this.router$$?.unsubscribe();
	}

	private setupShellRouter() {
		if (!this.documentRef.obgEventBus || this.router$$ || !this.lang) {
			return;
		}
		const singleLocale = this.startupContext.config.appSettings.isDefaultMarketCodeUrlDisabled;
		const locales = this.startupContext.config.appSettings.marketList.map(
			(m: { languageCode: string }) => m.languageCode
		);
		const i18nConfig = {
			singleLocale: singleLocale,
			defaultLocale: this.lang,
			allowedLocales: locales,
			localeToHtmlLang: locales.reduce(
				(acc: Record<string, string>, locale: string) => ({ ...acc, [locale]: locale }),
				{}
			),
			htmlLang: this.lang,
		};

		const { pageIdentifiers, routeTranslationMap } = this.buildPageIdentifiersAndRouteTranslationMap(
			this.router.config as RouteConfig[]
		);

		this.documentRef.obgEventBus?.dispatch(RouterLibCommand.Init, {
			i18nConfig: i18nConfig,
			locale: this.lang,
			pageIdentifiers,
			translationMap: routeTranslationMap,
			navigateFn: (url: string) => {
				const preparedUrl = singleLocale ? url : url.substring(url.slice(1).indexOf("/") + 2);

				this.store.dispatch(RouteAction.navigateByUrl(preparedUrl));
			},
		});

		this.router$$ = this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map((event: NavigationEnd) => event.url),
				switchMap(() => this.store.select(RouteSelector.getCurrent))
			)
			.subscribe((route) => {
				if (!route) {
					return;
				}

				const { pathName, name } = route;

				this.documentRef.obgEventBus?.dispatch(RouterLibCommand.UpdatePathName, { url: pathName });
				this.documentRef.obgEventBus?.dispatch(RouterLibCommand.RouterTransitionEnd, {
					routeName: name,
					outletName: "root",
				});
			});
	}

	private buildPageIdentifiersAndRouteTranslationMap(routes: RouteConfig[]): {
		pageIdentifiers: PageIdentifier[];
		routeTranslationMap: RouteTranslationMap;
	} {
		const pageIdentifiers: PageIdentifier[] = [];
		const routeTranslationMap: RouteTranslationMap = {
			[this.lang]: [],
		};

		routes
			.filter((route) => route.path !== "")
			.forEach((route) => {
				const originalPath = "/" + route.originalPath;
				const translatedPath = "/" + route.path;

				pageIdentifiers.push({
					pageId: route.name,
					routeString: originalPath,
				});

				routeTranslationMap[this.lang].push({
					urlLogical: originalPath,
					urlTranslated: translatedPath,
				});
			});

		return {
			pageIdentifiers,
			routeTranslationMap,
		};
	}
}
